const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/PDFDocument.tsx';
import { Document, Page, pdfjs } from 'react-pdf';
import React, { useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { css, cx } from '@emotion/css';
import { addSVGDrawingOptionsDefaults, DrawingArea, SVGDrawingOptions } from './DrawingArea';
import { KeyValuePair } from '../../defs/generalDefs';
import { PDFPageInfo } from './PDFPageInfo';
import { addDrawingToPDF } from '../../utils/pdf';
import { useTheme } from '@mui/material/styles';
import { useDidUpdateEffect } from '../../hooks/useDidUpdateEffect';
import { useDampenedWheelScroll } from '../../hooks/useDampenedWheelScroll';
import { usePdfPinchZoom } from './usePdfPinchZoom';
import { useVisiblePages } from './useVisiblePages';
import { usePdfPageSizes } from './usePdfPageSizes';
import { useDrawingUndoStack } from './useDrawingUndoStack';
import { usePDFViewer } from './PDFViewerProvider';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

require('pdfjs-dist/build/pdf.worker.min.mjs');
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

interface Props {
    className?: string;
    isSaving?: boolean;
    onSave?: (pdfASCIIString: string) => void;
    pdfASCIIString: string;
    svgDrawingOptions?: SVGDrawingOptions;
}

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            pdfDocumentWrapperCSS: css`
                position: relative;
                min-height: 0;
                // Creates a stacking context so that internal z-index values (react-pdf layers)
                // don't leak into the parent layout and paint over sibling shadows (e.g. MasterView).
                isolation: isolate;
            `,
            pdfDocumentScrollCSS: css`
                height: 100%;
                overflow: auto;
                background-color: ${theme.palette.grey['100']};
                padding: 20px 0;
                & .react-pdf__Document {
                    position: relative;

                    & > .react-pdf__Page,
                    & .PDFDocumentPageStage > .react-pdf__Page {
                        background-color: ${theme.palette.grey['100']} !important;
                    }

                    & > .react-pdf__Page > *,
                    & .PDFDocumentPageStage > .react-pdf__Page > * {
                        margin: auto;
                    }

                    & > .react-pdf__Page > .react-pdf__Page__textContent,
                    & .PDFDocumentPageStage > .react-pdf__Page > .react-pdf__Page__textContent {
                        // workaround for unexplained scrolling area (rendered elements were bigger than/positioned outside of the visible
                        // bits of the PDF)
                        overflow: hidden;
                    }

                    & *:last-child {
                        z-index: 2;
                    }
                }
            `,
            pdfGutterCanvasCSS: css`
                width: max-content;
                transition: opacity 120ms ease;
            `,
            pdfGutterPanCSS: css`
                padding-inline: 50%;
            `,
            pdfContentHiddenCSS: css`
                opacity: 0;
            `,
            pdfZoomWrapperCSS: css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: calc(${theme.spacing(1)} * var(--pdf-scale, 1));
                transform-origin: 0 0;
            `,
            pdfPageStageCSS: css`
                position: relative;
            `,
            pageInfoOverlayWrapperCSS: css`
                position: absolute;
                bottom: ${theme.spacing(2.25)};
                right: 0;
                left: 0;
                z-index: 4;
                display: flex;
                justify-content: center;
                pointer-events: none;
            `,
        }),
        [theme]
    );
}

export const PDFDocument = (props: Props) => {
    const { documentRef, setDocumentState, setDocumentChanged } = usePDFViewer();

    const [docLoadVersion, setDocLoadVersion] = useState(0);

    const isSaving = props.isSaving ?? false;
    const onSave = props.onSave;

    const svgDrawingOptions = addSVGDrawingOptionsDefaults(props.svgDrawingOptions);

    const drawingAreaRefs = useRef<Map<number, React.RefObject<any>>>(new Map());

    const getOrCreateDrawingAreaRef = (page: number): React.RefObject<any> => {
        if (!drawingAreaRefs.current.has(page)) {
            drawingAreaRefs.current.set(page, React.createRef());
        }
        return drawingAreaRefs.current.get(page)!;
    };
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const zoomWrapperRef = useRef<HTMLDivElement>(null);
    const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const pageSizes = usePdfPageSizes();
    const [canvasReadyPages, setCanvasReadyPages] = useState<Set<number>>(new Set());

    const [enableDrawing, setEnableDrawing] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);

    // Weird workaround.... obviously KISIM delivers VERY strange base64 strings.... ?! pdf-doc doesn't like them...
    const initialPdfString = props.pdfASCIIString ? btoa(atob(props.pdfASCIIString)) : '';

    // Keep a stable ref to the original so reset() can always return to the pre-drawing state.
    const originalPdfRef = useRef(initialPdfString);
    const [pdfASCIIString, setPDFASCIIString] = useState(initialPdfString);
    const [scale, setScale] = useState(100);
    const [pageCount, setPageCount] = useState(0);

    const file = 'data:application/pdf;base64,' + (pdfASCIIString || '');

    const classes = useCSSClasses();

    const { onFocalPageRendered, requestZoom } = usePdfPinchZoom({
        enabled: !enableDrawing,
        pageRefs,
        scale,
        scrollContainerRef,
        setScale,
        zoomWrapperRef,
    });

    const { currentPage, visiblePageSet } = useVisiblePages({
        enabled: pageCount > 0,
        pageCount,
        pageRefs,
        scrollContainerRef,
        zoomWrapperRef,
        documentKey: docLoadVersion,
    });

    const {
        handleDrawingChange,
        hasActiveDrawing,
        reset: resetDrawTracking,
        undo: undoDrawing,
    } = useDrawingUndoStack({
        drawingAreaRefs,
    });

    // Dampen scroll speed to help with performance of large PDFs
    useDampenedWheelScroll(scrollContainerRef, {
        factor: 0.3,
        enabled: !enableDrawing,
    });

    // Sync currentPage → pageNumber state
    useEffect(() => {
        setPageNumber(currentPage);
    }, [currentPage]);

    const handleLoadSuccess = (data: KeyValuePair) => {
        setPageCount(data.numPages);
        setDocLoadVersion(v => v + 1);
        pageSizes.seedFromDocument(data);
    };

    const registerPageRef = (pageNumber: number) => (el: HTMLDivElement | null) => {
        if (el) {
            el.dataset.pageNumber = String(pageNumber);
            pageRefs.current.set(pageNumber, el);
        } else {
            pageRefs.current.delete(pageNumber);
        }
    };

    const recordPageHeight = (page: number) => {
        const el = pageRefs.current.get(page);
        if (el) {
            pageSizes.recordRenderedHeight(page, el.offsetHeight, scale);
            setCanvasReadyPages(prev => {
                if (prev.has(page)) return prev; // no re-render if already in set
                const next = new Set(prev);
                next.add(page);
                return next;
            });
        }
    };

    const pendingScrollRestore = useRef<{ scrollLeft: number; scrollTop: number; targetPage: number } | null>(null);

    const [isReady, setIsReady] = useState(false);
    useDidUpdateEffect(() => {
        setIsReady(false);
    }, [initialPdfString]);

    // Pages are left-aligned in the zoom wrapper; the gutter (pdfGutterCanvasCSS) adds
    // `padding-inline: 50%` of scroll room and this centers within it once per source PDF.
    const centeredForSourceRef = useRef<string | null>(null);
    useLayoutEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        if (centeredForSourceRef.current === initialPdfString) return;
        // Wait until at least one page has actually rendered
        // to compute the centered scroll position.
        if (canvasReadyPages.size === 0) return;
        if (el.scrollWidth > el.clientWidth) {
            el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
        }
        centeredForSourceRef.current = initialPdfString;
        setIsReady(true);
    }, [initialPdfString, canvasReadyPages]);

    const getPageProps = (page: number) => ({
        loading: '',
        pageNumber: page,
        scale: scale / 100,
        // Cap device pixel ratio to cap memory usage in high zoom
        devicePixelRatio: Math.min(window.devicePixelRatio || 1, 1.25),
        onRenderSuccess: () => {
            recordPageHeight(page);
            onFocalPageRendered(page);

            // restore scroll position when drawings were saved and pdf string changed
            if (pendingScrollRestore.current !== null && scrollContainerRef.current) {
                // Restore both axes
                scrollContainerRef.current.scrollLeft = pendingScrollRestore.current.scrollLeft;
                scrollContainerRef.current.scrollTop = pendingScrollRestore.current.scrollTop;
                // Stop re-applying once the page the user was on has rendered at real height.
                if (page === pendingScrollRestore.current.targetPage) {
                    pendingScrollRestore.current = null;
                }
            }
        },
    });

    const saveAllDrawings = async (): Promise<string> => {
        if (drawingAreaRefs.current.size === 0) return pdfASCIIString;

        const drawings: Array<[number, string]> = [];
        drawingAreaRefs.current.forEach((ref, page) => {
            const svg = ref?.current?.getSvgXml() ?? '';
            if (svg.indexOf('<path') > 0) {
                drawings.push([page, svg]);
            }
            ref?.current?.clear();
        });

        resetDrawTracking();

        if (drawings.length === 0) return pdfASCIIString;

        let pdf = pdfASCIIString;
        for (const [page, svg] of drawings) {
            pdf = await addDrawingToPDF(pdf, svg, svgDrawingOptions, page, scale);
        }

        pendingScrollRestore.current = scrollContainerRef.current
            ? {
                  scrollLeft: scrollContainerRef.current.scrollLeft,
                  scrollTop: scrollContainerRef.current.scrollTop,
                  targetPage: pageNumber,
              }
            : null;
        setPDFASCIIString(pdf);
        return pdf;
    };

    const pendingSaveRef = useRef(false);

    useDidUpdateEffect(() => {
        if (enableDrawing) return;

        saveAllDrawings()
            .then(newPdf => {
                if (pendingSaveRef.current) {
                    pendingSaveRef.current = false;
                    onSave?.(newPdf);
                }
            })
            .catch(err => console.error('Failed to save drawings', err));
    }, [enableDrawing]);

    // disable touch events and scrolling in drawing mode
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!(el && enableDrawing)) return;

        el.style.touchAction = 'none';

        const preventScroll = (e: WheelEvent) => e.preventDefault();
        el.addEventListener('wheel', preventScroll, { passive: false });

        return () => {
            el.removeEventListener('wheel', preventScroll);
            el.style.touchAction = '';
        };
    }, [enableDrawing]);

    useDidUpdateEffect(() => {
        const isChanged = pdfASCIIString !== originalPdfRef.current;
        setDocumentChanged(isChanged);
        // When the PDF string changes (drawing committed), the Page components re-render
        // and get new canvas elements. Clear canvasReadyPages so onRenderSuccess fires a
        // fresh render that picks up the new canvas — otherwise DrawingArea silently loses
        // its baseComponent and drawing stops working.
        setCanvasReadyPages(new Set());
        resetDrawTracking();
    }, [pdfASCIIString]);

    useEffect(() => {
        setDocumentState({
            enableDrawing,
            hasActiveDrawing,
            isSaving,
            pageCount,
            pageNumber,
            pdfASCIIString,
            scale,
        });
    }, [enableDrawing, hasActiveDrawing, isSaving, pageCount, pageNumber, pdfASCIIString, scale, setDocumentState]);

    useImperativeHandle(documentRef, () => ({
        disableDrawing: () => setEnableDrawing(false),
        enableDrawing: () => setEnableDrawing(true),
        save: async () => {
            if (enableDrawing) {
                const newPdf = await saveAllDrawings();
                setEnableDrawing(false);
                onSave?.(newPdf ?? pdfASCIIString);
                resetDrawTracking();
                return;
            }
            onSave?.(pdfASCIIString);
        },
        setPage: (newPage: number) => {
            const pageEl = pageRefs.current.get(newPage);
            if (pageEl) {
                pageEl.scrollIntoView({ behavior: 'instant', block: 'start' });
            }
            setPageNumber(newPage);
        },
        setScale: (scale: number) => requestZoom(scale),
        undo: () => undoDrawing(),
    }));

    const renderScrollablePages = () =>
        Array.from({ length: pageCount }, (_, i) => {
            const page = i + 1;
            const isVisible = visiblePageSet.has(page);
            const { height: placeholderHeight, width: placeholderWidth } = pageSizes.placeholderSize(page, scale);
            const canvas = pageRefs.current.get(page)?.querySelector('canvas') ?? null;
            const isCanvasReady = canvasReadyPages.has(page);

            return (
                <div
                    key={page}
                    ref={registerPageRef(page)}
                    className={cx('PDFDocumentPageStage', classes.pdfPageStageCSS)}
                    style={{ minHeight: placeholderHeight, minWidth: placeholderWidth }}
                >
                    {isVisible && (
                        <>
                            <Page {...getPageProps(page)} />
                            <DrawingArea
                                ref={getOrCreateDrawingAreaRef(page)}
                                enabled={enableDrawing && isVisible}
                                baseComponent={
                                    isCanvasReady
                                        ? canvas
                                        : null /* don't render DrawingArea until canvas is ready to avoid misalignment issues */
                                }
                                stageComponent={pageRefs.current.get(page) ?? null}
                                svgDrawingOptions={svgDrawingOptions}
                                scale={scale}
                                pageNumber={page}
                                onChange={() => handleDrawingChange(page)}
                            />
                        </>
                    )}
                </div>
            );
        });

    return (
        <div className={cx(classes.pdfDocumentWrapperCSS, props.className)}>
            <div className={classes.pageInfoOverlayWrapperCSS}>
                <PDFPageInfo pageCount={pageCount} pageNumber={pageNumber} />
            </div>
            <div ref={scrollContainerRef} className={classes.pdfDocumentScrollCSS}>
                <Document file={file} loading="" onLoadSuccess={handleLoadSuccess}>
                    <div
                        className={cx(
                            classes.pdfGutterCanvasCSS,
                            classes.pdfGutterPanCSS,
                            !isReady && classes.pdfContentHiddenCSS
                        )}
                        style={
                            {
                                '--pdf-scale': scale / 100,
                                '--pdf-page-width': `${pageSizes.placeholderSize(1, 100).width}px`,
                            } as React.CSSProperties
                        }
                    >
                        <div ref={zoomWrapperRef} className={classes.pdfZoomWrapperCSS}>
                            {renderScrollablePages()}
                        </div>
                    </div>
                </Document>
            </div>
        </div>
    );
};
